//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import { PojoSet } from 'pojo-sets';

import { FacebookScheduleAction } from '@facebook/actions/facebookScheduleActions';
import {
  FACEBOOK_SCHEDULE_ACTIONS,
  FACEBOOK_SCHEDULE_POST_TYPES,
  FACEBOOK_SCHEDULE_STEPS,
} from '@facebook/constants/facebookConstants';
import { FacebookPostDetailsValidationError } from '@facebook/models/facebookPostDetailsModel';
import { IFacebookSchedule } from '@facebook/models/facebookStateModel';

const initialState: IFacebookSchedule = {
  currentStep: FACEBOOK_SCHEDULE_STEPS.SUMMARY,
  targetCuid: undefined,
  linkMetadataRecord: {},
  pagesSearches: {
    data: {},
  },
  taggedPages: {
    data: {},
    invalidPages: {},
  },
  post: {
    postBodyLinkIds: [],
    postId: undefined,
    caption: '',
    postUrl: undefined,
    postStatus: undefined,
    postType: undefined,
    mediaItems: [],
    scheduleDateTime: undefined,
    scheduleDateTimeIsValid: false,
    originalScheduleDateTime: undefined,
    shortLinks: {},
  },
  flags: {
    isFetchingLinkMetadata: false,
    isFetchingMediaItems: false,
    isFetchingScheduledPost: false,
    isFetchingShortLink: false,
    isFetchingShowroomData: false,
    isMediaErrorModalOpen: false,
    isPosting: false,
    isDrafting: false,
    isVideoTooLong: false,
  },
  errors: {
    initialPostDetailsError: undefined,
    getPagesSearchErrors: [],
    getPageByIdErrors: [],
    schedulePostErrors: [],
    scheduleDraftErrors: [],
    shortLinkErrors: [],
    validationErrors: PojoSet.empty<FacebookPostDetailsValidationError>(),
  },
};

export default function FacebookScheduleReducer(
  state: IFacebookSchedule = initialState,
  action: FacebookScheduleAction,
): IFacebookSchedule {
  switch (action.type) {
    case FACEBOOK_SCHEDULE_ACTIONS.REQUEST_SCHEDULE_POST: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isPosting: true,
          isDrafting: false,
        },
        errors: {
          ...state.errors,
          scheduleDraftErrors: [],
          schedulePostErrors: [],
          initialPostDetailsError: undefined,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECEIVE_SCHEDULE_POST_RESULT_SUCCEEDED: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingScheduledPost: false,
          isFetchingLinkMetadata: false,
          isPosting: false,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECEIVE_SCHEDULE_POST_RESULT_FAILED: {
      return {
        ...state,
        errors: {
          ...state.errors,
          schedulePostErrors: [
            ...state.errors.schedulePostErrors,
            action.failureMessage,
          ],
        },
        flags: {
          ...state.flags,
          isPosting: false,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.REQUEST_SCHEDULE_DRAFT: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isDrafting: true,
          isPosting: false,
        },
        errors: {
          ...state.errors,
          scheduleDraftErrors: [],
          schedulePostErrors: [],
          validationErrors: PojoSet.empty<FacebookPostDetailsValidationError>(),
          initialPostDetailsError: undefined,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECEIVE_SCHEDULE_DRAFT_RESULT_SUCCEEDED: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isDrafting: false,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECEIVE_SCHEDULE_DRAFT_RESULT_FAILED: {
      return {
        ...state,
        errors: {
          ...state.errors,
          scheduleDraftErrors: [
            ...state.errors.scheduleDraftErrors,
            action.failureMessage,
          ],
        },
        flags: {
          ...state.flags,
          isDrafting: false,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECIEVE_PAGES_SEARCH_SUCCEEDED: {
      return {
        ...state,
        pagesSearches: {
          data: { ...state.pagesSearches.data, ...action.pages },
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECIEVE_PAGES_SEARCH_FAILED: {
      return {
        ...state,
        errors: {
          ...state.errors,
          getPagesSearchErrors: [
            ...state.errors.getPagesSearchErrors,
            action.failureMessage,
          ],
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.REQUEST_LINK_METADATA: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingLinkMetadata: true,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECEIVE_LINK_METADATA: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingLinkMetadata: false,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.SET_LINK_METADATA_RECORD: {
      return {
        ...state,
        linkMetadataRecord: {
          ...state.linkMetadataRecord,
          ...action.linkMetadataRecord,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.REQUEST_SCHEDULED_POST: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingScheduledPost: true,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECEIVE_SCHEDULED_POST_SUCCEEDED: {
      return {
        ...state,
        post: action.post,
        flags: {
          ...state.flags,
          isFetchingScheduledPost: false,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECEIVE_SCHEDULED_POST_FAILED: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingScheduledPost: false,
        },
        errors: {
          ...state.errors,
          initialPostDetailsError: action.message,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.REQUEST_SHORT_LINK: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingShortLink: true,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECEIVE_SHORT_LINK_SUCCEEDED: {
      return {
        ...state,
        post: {
          ...state.post,
          shortLinks: { ...state.post.shortLinks, ...action.shortLinks },
        },
        flags: {
          ...state.flags,
          isFetchingShortLink: false,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECEIVE_SHORT_LINK_FAILED: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingShortLink: false,
        },
        errors: {
          ...state.errors,
          shortLinkErrors: [...state.errors.shortLinkErrors, action.message],
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECEIVE_TAGGED_PAGE: {
      return {
        ...state,
        taggedPages: {
          ...state.taggedPages,
          data: { ...state.taggedPages.data, ...action.page },
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RECIEVE_GET_PAGE_BY_ID_FAILED: {
      return {
        ...state,
        taggedPages: {
          ...state.taggedPages,
          invalidPages: {
            ...state.taggedPages.invalidPages,
            [action.pageId]: action.failureMessage,
          },
        },
        errors: {
          ...state.errors,
          getPageByIdErrors: [
            ...state.errors.getPageByIdErrors,
            action.failureMessage,
          ],
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.REQUEST_MEDIA_ITEMS: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isFetchingMediaItems: true,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.UPDATE_POST_URL: {
      return {
        ...state,
        post: {
          ...state.post,
          postUrl: action.postUrl,
        },
        errors: {
          ...state.errors,
          validationErrors: PojoSet.remove(
            state.errors.validationErrors,
            FacebookPostDetailsValidationError.NoMediaOrLink,
          ),
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.UPDATE_TARGET_CUID: {
      return {
        ...state,
        targetCuid: action.targetCuid,
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.UPDATE_STEP: {
      return {
        ...state,
        currentStep: action.step,
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.UPDATE_CAPTION: {
      return {
        ...state,
        post: {
          ...state.post,
          caption: action.caption,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.UPDATE_MEDIA_ITEMS: {
      return {
        ...state,
        post: {
          ...state.post,
          mediaItems: [...state.post.mediaItems, ...action.items],
        },
        flags: {
          ...state.flags,
          isFetchingMediaItems: false,
          isVideoTooLong: false,
        },
        errors: {
          ...state.errors,
          validationErrors: PojoSet.difference(
            state.errors.validationErrors,
            PojoSet.from([
              FacebookPostDetailsValidationError.NoMediaOrLink,
              FacebookPostDetailsValidationError.ImageTooLarge,
              FacebookPostDetailsValidationError.VideoTooLong,
            ]),
          ),
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.UPDATE_MEDIA_ITEM_IN_PLACE: {
      const updatedMediaItems = state.post.mediaItems.map((item) => {
        if (item.id === action.item.id) {
          return action.item;
        }

        return item;
      });

      return {
        ...state,
        post: {
          ...state.post,
          mediaItems: updatedMediaItems,
        },
        flags: {
          ...state.flags,
          isFetchingMediaItems: false,
          isVideoTooLong: false,
        },
        errors: {
          ...state.errors,
          validationErrors: PojoSet.difference(
            state.errors.validationErrors,
            PojoSet.from([
              FacebookPostDetailsValidationError.NoMediaOrLink,
              FacebookPostDetailsValidationError.ImageTooLarge,
              FacebookPostDetailsValidationError.VideoTooLong,
            ]),
          ),
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.UPDATE_SCHEDULE_DATE: {
      return {
        ...state,
        post: {
          ...state.post,
          scheduleDateTime: action.date,
          scheduleDateTimeIsValid: action.isValid,
        },
        errors: {
          ...state.errors,
          validationErrors: PojoSet.difference(
            state.errors.validationErrors,
            PojoSet.from([
              FacebookPostDetailsValidationError.NoPostTime,
              FacebookPostDetailsValidationError.PostTimeInPast,
            ]),
          ),
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.UPDATE_POST_TYPE: {
      return {
        ...state,
        post: {
          ...state.post,
          postType: action.postType,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.UPDATE_POST_DETAILS_FOR_EDIT: {
      return {
        ...state,
        post: action.post,
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RESET_MEDIA: {
      return {
        ...state,
        post: {
          ...state.post,
          mediaItems: [],
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RESET_POST_URL: {
      return {
        ...state,
        post: {
          ...state.post,
          postUrl: undefined,
          postType:
            state.post.postType === FACEBOOK_SCHEDULE_POST_TYPES.LINK
              ? undefined
              : state.post.postType,
        },
        flags: {
          ...state.flags,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RESET_POST_DETAILS: {
      return {
        ...state,
        post: initialState.post,
        flags: initialState.flags,
        errors: initialState.errors,
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RESET_SCHEDULE_STATE: {
      return {
        ...state,
        currentStep: initialState.currentStep,
        post: initialState.post,
        flags: initialState.flags,
        errors: initialState.errors,
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.RESET_SHOWROOM_DATA: {
      return {
        ...state,
        post: {
          ...state.post,
          postUrl: undefined,
        },
        flags: {
          ...state.flags,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.SET_IS_ERROR_MODAL_OPEN: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isMediaErrorModalOpen: action.isOpen,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.SET_IS_VIDEO_TOO_LONG: {
      return {
        ...state,
        flags: {
          ...state.flags,
          isVideoTooLong: action.isVideoTooLong,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.SET_VALIDATION_ERRORS: {
      return {
        ...state,
        errors: {
          ...state.errors,
          validationErrors: action.validationErrors,
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.REMOVE_SELECTED_CUID: {
      const updatedMediaItems = state.post.mediaItems.filter(
        (c) => c.id !== action.itemId,
      );
      const updatedPostType = (() => {
        if (updatedMediaItems.length === 1) {
          if (
            state.post.postType === FACEBOOK_SCHEDULE_POST_TYPES.SINGLE_VIDEO
          ) {
            return state.post.postType;
          } else {
            return FACEBOOK_SCHEDULE_POST_TYPES.SINGLE_IMAGE;
          }
        } else if (updatedMediaItems.length === 0) {
          return state.post.postUrl
            ? FACEBOOK_SCHEDULE_POST_TYPES.LINK
            : undefined;
        } else {
          return state.post.postType;
        }
      })();

      return {
        ...state,
        post: {
          ...state.post,
          mediaItems: updatedMediaItems,
          postType: updatedPostType,
        },
        errors: {
          ...state.errors,
          validationErrors: PojoSet.difference(
            state.errors.validationErrors,
            PojoSet.from([
              FacebookPostDetailsValidationError.VideoTooLong,
              FacebookPostDetailsValidationError.ImageTooLarge,
            ]),
          ),
        },
      };
    }
    case FACEBOOK_SCHEDULE_ACTIONS.UPDATE_SCHEDULING_ENTRYPOINT:
      return {
        ...state,
        schedulingEntryPoint: action.state,
      };
    default:
      return state;
  }
}
